import { Popover } from "react-tiny-popover";
import BellIcon from "../../assets/alerts/bellImportant.svg?react";
import { useContext, useEffect, useRef, useState } from "react";
import NfsButton from "../genericComponents/NfsButton/NfsButton";

import "./Alerts.css";
import AlertsList from "./AlertsList";
import {
    getAnalysisFailedJobs,
    getControlDeviationJobs,
    getQA1Jobs,
    getQA2Jobs,
    getRejectedPlanJobs,
} from "../../utils/jobsUtils";
import { useSelector } from "react-redux";
import { StoreState } from "../../store/rootReducer";
import { UserContext, VersionContext } from "../../Context";
import { getExpiredEFS } from "../../utils/efsUtils";
import { isDefaultVersion } from "../../utils/versioningUtils";
import type Graphic from "@arcgis/core/Graphic";

const Alerts = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [notificationCount, setNotificationCount] = useState<number>(0);

    const intervalRef = useRef<NodeJS.Timeout>(null);

    const [qa1Alerts, setQa1Alerts] = useState<Graphic[]>([]);
    const [qa2Alerts, setQa2Alerts] = useState<Graphic[]>([]);
    const [rejectedPlanAlerts, setRejectedPlanAlerts] = useState<Graphic[]>([]);
    const [controlDeviationAlerts, setControlDeviationAlerts] = useState<Graphic[]>([]);
    const [analysisFailedAlerts, setAnalysisFailedAlerts] = useState<Graphic[]>([]);
    const [expiredEfs, setExpiredEfs] = useState<{ [layerName: string]: Graphic[] }>({});

    const triggerAlertUpdate = useSelector((state: StoreState) => state.triggers.triggerAlertsRefresh);

    const userContext = useContext(UserContext);
    const user = userContext?.user.value;

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    // Create a ref to store the latest activeVersion value
    const activeVersionRef = useRef(activeVersion);
    const versionContext = useContext(VersionContext);

    // Update the ref whenever activeVersion changes
    useEffect(() => {
        activeVersionRef.current = activeVersion;
    }, [activeVersion]);

    const updateAlerts = () => {
        if (!user) {
            return;
        }

        // Use the ref to access the latest activeVersion value
        const currentActiveVersion = activeVersionRef.current;

        getQA1Jobs("othersOnly", user.username).then((jobs) => {
            if (jobs) {
                setQa1Alerts(jobs);
            } else {
                setQa1Alerts([]);
            }
        });
        getQA2Jobs("all", user.username).then((jobs) => {
            if (jobs) {
                setQa2Alerts(jobs);
            } else {
                setQa2Alerts([]);
            }
        });
        getRejectedPlanJobs("ownOnly", user.username).then((jobs) => {
            if (jobs) {
                setRejectedPlanAlerts(jobs);
            } else {
                setRejectedPlanAlerts([]);
            }
        });
        getAnalysisFailedJobs("all", user.username).then((jobs) => {
            if (jobs) {
                setAnalysisFailedAlerts(jobs);
            } else {
                setAnalysisFailedAlerts([]);
            }
        });
        getControlDeviationJobs("all", user.username).then((jobs) => {
            if (jobs) {
                setControlDeviationAlerts(jobs);
            } else {
                setControlDeviationAlerts([]);
            }
        });

        // Check if we're on the default version and should fetch expired EFS
        const isDefault =
            versionContext &&
            currentActiveVersion &&
            versionContext.versionServiceRef.value.current &&
            isDefaultVersion(currentActiveVersion, versionContext.versionServiceRef.value.current);

        if (isDefault) {
            getExpiredEFS()
                .then((efs) => {
                    if (efs) {
                        setExpiredEfs(efs);
                    } else {
                        setExpiredEfs({});
                    }
                })
                .catch((error) => {
                    console.error("Error fetching expired EFS:", error);
                    setExpiredEfs({});
                });
        } else {
            setExpiredEfs({});
        }
    };

    useEffect(() => {
        let combinedCount = qa1Alerts.length + qa2Alerts.length + rejectedPlanAlerts.length;
        const expiredEfsCount = Object.values(expiredEfs).reduce((acc, layerEfs) => acc + layerEfs.length, 0);
        combinedCount += expiredEfsCount;
        setNotificationCount(combinedCount);
    }, [qa1Alerts, qa2Alerts, rejectedPlanAlerts, expiredEfs]);

    useEffect(() => {
        updateAlerts();
    }, [triggerAlertUpdate, activeVersion]);

    useEffect(() => {
        updateAlerts();
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            updateAlerts();
        }, 60000);

        // Cleanup interval on component unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [user, versionContext]);

    return (
        <Popover
            isOpen={isOpen}
            containerStyle={{ zIndex: "10" }}
            positions={["bottom"]}
            content={
                <AlertsList
                    qa1Alerts={qa1Alerts}
                    qa2Alerts={qa2Alerts}
                    controlDeviationAlerts={controlDeviationAlerts}
                    analysisFailedAlerts={analysisFailedAlerts}
                    rejectedPlanAlerts={rejectedPlanAlerts}
                    expiredEfs={expiredEfs}
                    closeAlerts={() => setIsOpen(false)}
                />
            }
            onClickOutside={() => setIsOpen(false)}
        >
            <NfsButton className="pos-relative" onClick={() => setIsOpen(!isOpen)} round>
                <BellIcon />
                {notificationCount > 0 && <div className="alert-count">{notificationCount}</div>}
            </NfsButton>
        </Popover>
    );
};

export default Alerts;
